/**
 * Tenant List Page - Admin
 * Manage all tenants/clients on the platform - Full CRUD support
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Users,
  Activity,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
  RefreshCw,
  Loader2,
  X,
  Key,
  Copy,
  Check,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import TenantDetailDrawer from '../../components/admin/TenantDetailDrawer';
import { useAllTenants } from '../../hooks/useTenant';
import tenantService from '../../services/tenantService';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

// Tenant Form Modal Component
function TenantFormModal({ isOpen, onClose, tenant, onSubmit, isSubmitting }) {
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    slug: tenant?.slug || '',
    status: tenant?.status || 'active',
    plan: tenant?.plan || 'starter',
    billing_type: tenant?.billing_type || 'token_based',
    contact_email: tenant?.contact_email || '',
    contact_name: tenant?.contact_name || '',
    primary_color: tenant?.branding?.primary_color || '#2563eb',
    max_users: tenant?.settings?.max_users || 10,
  });
  const [errors, setErrors] = useState({});

  const isEdit = !!tenant?.id;

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(formData.slug)) newErrors.slug = 'Slug must be lowercase with hyphens only';
    if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      name: formData.name,
      slug: formData.slug,
      status: formData.status,
      plan: formData.plan,
      billing_type: formData.billing_type,
      contact_email: formData.contact_email,
      contact_name: formData.contact_name,
      branding: { primary_color: formData.primary_color },
      settings: { max_users: parseInt(formData.max_users) || 10 },
    };

    onSubmit(submitData, tenant?.id);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {isEdit ? 'Edit Tenant' : 'Create Tenant'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData(d => ({
                    ...d,
                    name: e.target.value,
                    slug: isEdit ? d.slug : generateSlug(e.target.value),
                  }));
                }}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white ${errors.name ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                placeholder="Acme Transport"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug (ID)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(d => ({ ...d, slug: e.target.value.toLowerCase() }))}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white ${errors.slug ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                placeholder="acme-transport"
                disabled={isEdit}
              />
              {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(d => ({ ...d, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData(d => ({ ...d, plan: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
              >
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billing Type</label>
              <select
                value={formData.billing_type}
                onChange={(e) => setFormData(d => ({ ...d, billing_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
              >
                <option value="token_based">Token-Based</option>
                <option value="post_paid">Post-Paid (Enterprise)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Users</label>
              <input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData(d => ({ ...d, max_users: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
                min="1"
                max="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData(d => ({ ...d, contact_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData(d => ({ ...d, contact_email: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white ${errors.contact_email ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                placeholder="contact@company.com"
              />
              {errors.contact_email && <p className="text-red-500 text-xs mt-1">{errors.contact_email}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData(d => ({ ...d, primary_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => setFormData(d => ({ ...d, primary_color: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
                  placeholder="#2563eb"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteConfirmModal({ isOpen, onClose, tenant, onConfirm, isDeleting }) {
  if (!isOpen || !tenant) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm">
        <div className="p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Delete Tenant</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Are you sure you want to delete <strong>{tenant.name}</strong>? This will remove all associated users and data.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
            <Button variant="danger" onClick={() => onConfirm(tenant.id)} disabled={isDeleting} className="flex-1">
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// API Key Management Modal Component
function ApiKeyManagementModal({ isOpen, onClose, tenant }) {
  const [copied, setCopied] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const queryClient = useQueryClient();

  // Fetch metadata when modal opens
  const { data: metadata, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-api-key', tenant?.id],
    queryFn: () => tenantService.getApiKeyMetadata(tenant.id),
    enabled: isOpen && !!tenant?.id,
    retry: false,
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: () => tenantService.generateApiKey(tenant.id),
    onSuccess: (data) => {
      setGeneratedKey(data.api_key);
      queryClient.invalidateQueries(['admin-api-key', tenant.id]);
    },
    onError: (error) => {
      console.error('API key operation failed:', error);
    },
  });

  // Revoke mutation
  const revokeMutation = useMutation({
    mutationFn: () => tenantService.revokeApiKey(tenant.id),
    onSuccess: () => {
      setShowRevokeConfirm(false);
      queryClient.invalidateQueries(['admin-api-key', tenant.id]);
    },
    onError: (error) => {
      console.error('API key operation failed:', error);
    },
  });

  const handleCopy = async () => {
    if (generatedKey) {
      try {
        await navigator.clipboard.writeText(generatedKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleClose = () => {
    setGeneratedKey(null);
    setCopied(false);
    setShowRevokeConfirm(false);
    onClose();
  };

  if (!isOpen || !tenant) return null;

  const status = metadata?.status || 'none';
  const is404 = error?.response?.status === 404;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              API Key - {tenant.name}
            </h3>
          </div>
          <button onClick={handleClose} aria-label="Close" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          )}

          {/* Error: No billing plan */}
          {is404 && (
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">No billing plan configured</p>
              <p className="text-sm text-gray-500">Create a billing plan in Billing Configuration first.</p>
            </div>
          )}

          {/* Generated Key Display */}
          {generatedKey && (
            <div className="space-y-4">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-400 font-medium mb-2">
                  API Key Generated Successfully
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm font-mono break-all">
                    {generatedKey}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  <strong>Warning:</strong> Store this key securely. It will not be shown again.
                </p>
              </div>
              <Button variant="primary" onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          )}

          {/* Status: None */}
          {!isLoading && !is404 && !generatedKey && status === 'none' && (
            <div className="text-center py-6">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">No API key configured for this tenant.</p>
              <Button
                variant="primary"
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Generate API Key
              </Button>
            </div>
          )}

          {/* Status: Active */}
          {!isLoading && !is404 && !generatedKey && status === 'active' && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">Key Prefix</span>
                  <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
                    {metadata?.prefix || 'srop_****'}
                  </code>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">Created</span>
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    {metadata?.created_at ? new Date(metadata.created_at).toLocaleDateString() : '-'}
                  </span>
                </div>
                {metadata?.created_by && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Created By</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">{metadata.created_by}</span>
                  </div>
                )}
              </div>

              {showRevokeConfirm ? (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                    This will immediately invalidate the API key. Any integrations using this key will stop working.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setShowRevokeConfirm(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => revokeMutation.mutate()}
                      disabled={revokeMutation.isPending}
                      className="flex-1"
                    >
                      {revokeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Revoke Key
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending}
                    className="flex-1"
                  >
                    {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Regenerate
                  </Button>
                  <Button variant="danger" onClick={() => setShowRevokeConfirm(true)} className="flex-1">
                    Revoke
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Status: Revoked */}
          {!isLoading && !is404 && !generatedKey && status === 'revoked' && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                    Revoked
                  </span>
                </div>
                {metadata?.revoked_at && (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">Revoked On</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">
                      {new Date(metadata.revoked_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {metadata?.revoked_by && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Revoked By</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">{metadata.revoked_by}</span>
                  </div>
                )}
              </div>
              <Button
                variant="primary"
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
                className="w-full"
              >
                {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                Generate New Key
              </Button>
            </div>
          )}

          {/* Generic Error */}
          {error && !is404 && (
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {error.response?.data?.error?.message || 'Failed to load API key information'}
              </p>
              <Button variant="secondary" onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TenantList() {
  const { data: tenants = [], isLoading, error, refetch } = useAllTenants();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [apiKeyTenant, setApiKeyTenant] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleCardClick = (tenant) => {
    setSelectedTenantId(tenant.id || tenant.slug);
    setIsDrawerOpen(true);
  };

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: tenantService.createTenant,
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-tenants']);
      setShowFormModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ tenantId, data }) => tenantService.updateTenant(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-tenants']);
      setShowFormModal(false);
      setEditingTenant(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tenantService.deleteTenant,
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-tenants']);
      setDeletingTenant(null);
    },
  });

  const handleFormSubmit = (data, tenantId) => {
    if (tenantId) {
      updateMutation.mutate({ tenantId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setShowFormModal(true);
    setActionMenuOpen(null);
  };

  const handleDelete = (tenant) => {
    setDeletingTenant(tenant);
    setActionMenuOpen(null);
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditingTenant(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading tenants...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to Load Tenants</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
          {error.response?.data?.error?.message || error.message || 'Unable to connect to the admin API.'}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
          Make sure the backend is running: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">python main.py serve</code>
        </p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <RefreshCw className="w-4 h-4 inline mr-2" /> Retry
        </button>
      </div>
    );
  }

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          tenant.slug?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const classes = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    };
    return classes[status] || classes.inactive;
  };

  const getPlanBadge = (plan) => {
    const classes = {
      enterprise: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      pro: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      starter: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    };
    return classes[plan] || classes.starter;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Tenants</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage all clients on the platform
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowFormModal(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </Card>

      {/* Tenant Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTenants.map((tenant) => (
          <Card key={tenant.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleCardClick(tenant)}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: tenant.branding?.primary_color || '#2563eb' }}
                >
                  {tenant.name?.[0] || 'T'}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white">{tenant.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">@{tenant.slug}</p>
                </div>
              </div>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionMenuOpen(actionMenuOpen === tenant.id ? null : tenant.id);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {actionMenuOpen === tenant.id && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                    <button
                      onClick={() => handleEdit(tenant)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" /> Edit Tenant
                    </button>
                    <button
                      onClick={() => {
                        setApiKeyTenant(tenant);
                        setActionMenuOpen(null);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Key className="w-4 h-4" /> Manage API Key
                    </button>
                    <button
                      onClick={() => handleDelete(tenant)}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete Tenant
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(tenant.status)}`}>
                {tenant.status}
              </span>
              <span className={`px-2 py-1 text-xs rounded-full ${getPlanBadge(tenant.plan)}`}>
                {tenant.plan}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Users className="w-4 h-4" />
                <span>{tenant.users_count || 0} users</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Activity className="w-4 h-4" />
                <span>{tenant.trips_count || 0} trips</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-800" onClick={(e) => e.stopPropagation()}>
              <Link
                to={`/admin/tenants/${tenant.id}`}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" /> View
              </Link>
              <button
                onClick={() => handleEdit(tenant)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" /> Edit
              </button>
            </div>
          </Card>
        ))}
      </div>

      {filteredTenants.length === 0 && !isLoading && (
        <Card className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-white">No tenants found</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {searchQuery ? 'Try a different search term' : 'Add your first tenant to get started'}
          </p>
        </Card>
      )}

      {/* Tenant Form Modal */}
      <TenantFormModal
        isOpen={showFormModal}
        onClose={handleCloseForm}
        tenant={editingTenant}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingTenant}
        onClose={() => setDeletingTenant(null)}
        tenant={deletingTenant}
        onConfirm={(tenantId) => deleteMutation.mutate(tenantId)}
        isDeleting={deleteMutation.isPending}
      />

      {/* API Key Management Modal */}
      <ApiKeyManagementModal
        isOpen={!!apiKeyTenant}
        onClose={() => setApiKeyTenant(null)}
        tenant={apiKeyTenant}
      />

      {/* Tenant Detail Drawer */}
      <TenantDetailDrawer
        tenantId={selectedTenantId}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
}

